import React from "react";
import { parse, stringify } from "qs";

import getStrainListHeader from "@/api/requests/getStrainListHeader";
import getStrainLists, {
  getStrainListsReturn,
  ITEMS_PER_STRAIN_PAGE,
} from "@/api/requests/getStrainLists";
import { getUserLocationData } from "@/api/requests/getUserLocation";
import { STRAIN_USER_REVIEW_DISCLAIMER } from "@/constants/disclaimers";
import { StrainListHeaders } from "@/custom-types/Directus";
import { Query } from "@/custom-types/Query";
import useGetStrainLists from "@/hooks/useGetStrainLists";
import useUserFavorites from "@/hooks/useUserFavorites";
import { createGetServerSideProps } from "@/utils/createGetServerSideProps";
import { normalizeLocationCookie } from "@/utils/getUserLocationFromCookie";
import { redirectIfPageOne } from "@/utils/redirectIfPageOne";
import { getCountryCodeHeader } from "@/utils/requestContext";

import Breadcrumb from "@/components/Breadcrumb";
import Breadcrumbs from "@/components/Breadcrumbs";
import { Facet } from "@/components/FilteredGrid";
import MedicalDisclaimers from "@/components/MedicalDisclaimers";
import { MenuPage } from "@/components/MenuFilters/types/MenuFilters";
import MenuFilters from "@/components/MenuFilters/v2";
import { useFilterQueryParams } from "@/components/MenuFilters/v2/hooks";
import StrainListCountSort from "@/components/StrainList/StrainListCountSort";
import StrainListHead from "@/components/StrainList/StrainListHead";
import StrainListHeader from "@/components/StrainList/StrainListHeader";
import StrainListV2 from "@/components/StrainListV2";

import pageFromQuery from "../../utils/pageFromQuery";

export type StrainsPageProps = {
  data: getStrainListsReturn;
  metadata: StrainListHeaders<string> | null;
};

const StrainsPage: React.FC<StrainsPageProps> = ({ metadata, data }) => {
  const { useInitiateFavorites } = useUserFavorites();
  useInitiateFavorites();

  const { data: results, loading } = useGetStrainLists(data);

  const {
    actions: { clearAllFilters, toggleFilter },
  } = useFilterQueryParams();

  const onClearAllClick = (facet?: Facet) => {
    if (!facet) {
      clearAllFilters();
      return;
    }
    toggleFilter(facet.key, facet.value);
  };

  return (
    <>
      <StrainListHead
        description={
          metadata?.meta_description ||
          "Browse the most comprehensive marijuana strain database on the web! Read strain reviews, discover new strains, and learn more about your favorites."
        }
        title={metadata?.meta_title || "All strains"}
      />

      <StrainListHeader
        hero={metadata}
        displayTitle={metadata?.title || "All strains"}
      />

      <MenuFilters
        availableFilters={results.availableFilters}
        menuPage={MenuPage.strains}
        loading={loading}
        mobileClassName="mt-md"
        onClearAllClick={onClearAllClick}
      >
        <StrainListCountSort
          totalCount={results.totalCount}
          loading={loading}
        />
        <MedicalDisclaimers
          usDisclaimer={STRAIN_USER_REVIEW_DISCLAIMER}
          className="mb-lg"
        />

        <StrainListV2
          adPath="/13212770/strain/explore_playlist"
          adTarget="strains"
          loading={loading}
          totalCount={results.totalCount}
          strains={results.strains}
        />
      </MenuFilters>
      <Breadcrumbs>
        <Breadcrumb currentLocation href="/strains">
          Strains
        </Breadcrumb>
      </Breadcrumbs>
    </>
  );
};

export const getServerSideProps = createGetServerSideProps<StrainsPageProps>(
  async (context) => {
    const countryCode = getCountryCodeHeader(context.req.headers);
    const userLocationData = await getUserLocationData(countryCode, context);
    const location = normalizeLocationCookie(userLocationData);

    const {
      query: { sort, ...query },
      resolvedUrl,
    } = context;

    const page = pageFromQuery(query.page);

    const { latitude: lat, longitude: lon } = location?.coordinates ?? {};

    const { filter: filterQueryParams = {} } = parse(stringify(query));

    const [metadata, data] = await Promise.all([
      getStrainListHeader(countryCode, "base", undefined),
      getStrainLists(
        filterQueryParams as Query,
        (page - 1) * ITEMS_PER_STRAIN_PAGE,
        ITEMS_PER_STRAIN_PAGE,
        { lat, lon },
        typeof sort === "string" ? sort : undefined,
      ),
    ]);

    if (
      page > 1 &&
      page > Math.ceil(data.metadata.totalCount / ITEMS_PER_STRAIN_PAGE)
    ) {
      return {
        notFound: true,
      };
    }

    const redirect = redirectIfPageOne(resolvedUrl, query.page);

    if (redirect) {
      return {
        redirect: {
          destination: redirect.destination,
          statusCode: redirect.statusCode,
        },
      };
    }

    return {
      props: {
        data,
        metadata,
      },
    };
  },
);

export default StrainsPage;
